/** @format */

import React, { useEffect, useRef, useState } from "react";
import {
    Box,
    Typography,
    Button,
    Container,
    Grid,
    Stack,
} from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import BoltIcon from "@mui/icons-material/Bolt";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import { colors } from "../../utility/types/constant";
import { Footer, SEO } from "../allComponents/AllComponents";
import { BusinessNavbar } from "../../components-v2/navbar/BusinessNavbar";

// ── Animations ───────────────────────────────────────────────────────────────

const gradientPan = keyframes`
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
`;
const floatY = keyframes`
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-16px); }
`;

// ── Styled components ────────────────────────────────────────────────────────

const PageWrapper = styled("div")({
    background: "#ffffff",
    overflowX: "hidden",
});

const Hero = styled("section")({
    position: "relative",
    background:
        "linear-gradient(125deg, #070e29 0%, #0d1636 45%, #16245c 100%)",
    backgroundSize: "200% 200%",
    animation: `${gradientPan} 20s ease infinite`,
    padding: "clamp(120px, 14vw, 168px) 20px clamp(120px, 14vw, 180px)",
    overflow: "hidden",
});

const GridOverlay = styled("div")({
    position: "absolute",
    inset: 0,
    backgroundImage:
        "linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px)",
    backgroundSize: "46px 46px",
    maskImage:
        "radial-gradient(ellipse 70% 60% at 50% 35%, #000 0%, transparent 80%)",
    WebkitMaskImage:
        "radial-gradient(ellipse 70% 60% at 50% 35%, #000 0%, transparent 80%)",
    pointerEvents: "none",
});

const Orb = styled("div")({
    position: "absolute",
    borderRadius: "50%",
    filter: "blur(60px)",
    pointerEvents: "none",
});

const Badge = styled("span")({
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    background: "rgba(255,204,0,0.12)",
    color: "#ffcc00",
    border: "1px solid rgba(255,204,0,0.30)",
    borderRadius: "100px",
    padding: "6px 16px",
    fontSize: "0.72rem",
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
});

const GoldSpan = styled("span")({
    background: "linear-gradient(135deg, #ffd02e 0%, #ffaa00 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
});

const GoldButton = styled(Button)({
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 700,
    borderRadius: "12px",
    padding: "15px 32px",
    textTransform: "none",
    fontSize: "1rem",
    background: "linear-gradient(135deg, #ffd02e 0%, #ffaa00 100%)",
    color: "#0d1636",
    boxShadow: "0 10px 30px rgba(255,176,0,0.42)",
    "&:hover": {
        background: "linear-gradient(135deg, #ffe05a 0%, #ffbb1a 100%)",
        boxShadow: "0 16px 40px rgba(255,176,0,0.55)",
        transform: "translateY(-2px)",
    },
    transition: "all 0.22s ease",
});

const OutlineButton = styled(Button)({
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 700,
    borderRadius: "12px",
    padding: "14px 30px",
    textTransform: "none",
    fontSize: "1rem",
    color: "#fff",
    border: "1px solid rgba(255,255,255,0.28)",
    background: "rgba(255,255,255,0.04)",
    "&:hover": {
        background: "rgba(255,255,255,0.1)",
        borderColor: "rgba(255,255,255,0.5)",
    },
    transition: "all 0.22s ease",
});

const PriceCard = styled("div")({
    position: "relative",
    background: "linear-gradient(165deg, #0b1334 0%, #122153 100%)",
    borderRadius: "26px",
    padding: "clamp(32px, 5vw, 52px)",
    border: "1px solid rgba(255,204,0,0.28)",
    boxShadow:
        "0 30px 80px rgba(4,13,69,0.45), inset 0 1px 0 rgba(255,255,255,0.06)",
    overflow: "hidden",
});

const Section = styled("section")<{ tone?: "light" | "tint" }>(
    ({ tone = "light" }) => ({
        position: "relative",
        overflow: "hidden",
        padding: "clamp(64px, 9vw, 104px) 0",
        background: tone === "tint" ? "#f6f8fc" : "#ffffff",
    }),
);

const SectionLabel = styled(Typography)({
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: colors.mainSecondary,
    marginBottom: "12px",
    textAlign: "center",
});

const SectionTitle = styled(Typography)({
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "clamp(1.6rem, 3.4vw, 2.5rem)",
    fontWeight: 800,
    color: colors.primary,
    lineHeight: 1.16,
    letterSpacing: "-0.02em",
    textAlign: "center",
    marginBottom: "14px",
});

const SectionSub = styled(Typography)({
    fontFamily: "'Inter', sans-serif",
    fontSize: "clamp(0.95rem, 1.6vw, 1.06rem)",
    color: "#5b6472",
    lineHeight: 1.72,
    textAlign: "center",
    maxWidth: "600px",
    margin: "0 auto 56px",
});

// ── Scroll reveal ────────────────────────────────────────────────────────────

const Reveal: React.FC<{
    children: React.ReactNode;
    delay?: number;
    y?: number;
    sx?: object;
}> = ({ children, delay = 0, y = 26, sx }) => {
    const ref = useRef<HTMLDivElement>(null);
    const [shown, setShown] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const ob = new IntersectionObserver(
            ([e]) => {
                if (e.isIntersecting) {
                    setShown(true);
                    ob.disconnect();
                }
            },
            { threshold: 0.15 },
        );
        ob.observe(el);
        return () => ob.disconnect();
    }, []);
    return (
        <Box
            ref={ref}
            sx={{
                opacity: shown ? 1 : 0,
                transform: shown ? "translateY(0)" : `translateY(${y}px)`,
                transition: `opacity 0.7s ease ${delay}s, transform 0.7s cubic-bezier(0.22,0.61,0.36,1) ${delay}s`,
                "@media (prefers-reduced-motion: reduce)": {
                    opacity: 1,
                    transform: "none",
                    transition: "none",
                },
                ...sx,
            }}
        >
            {children}
        </Box>
    );
};

// ── Data ─────────────────────────────────────────────────────────────────────

const INCLUDED_FEATURES: { label: string; standout?: boolean }[] = [
    {
        label: "Collections Autopilot: runs your follow-ups automatically and only pulls you in when a human is truly needed",
        standout: true,
    },
    {
        label: "Two-way QuickBooks Online sync, invoices, customers & payments always current",
    },
    {
        label: "Automated email & SMS reminder sequences that run on your schedule",
    },
    {
        label: "Automatically reads customer replies, disputes, promises & ‘already paid’ claims and routes them for you",
        standout: true,
    },
    {
        label: "Real-time Recovery Impact dashboard, see revenue recovered & hours saved",
    },
    {
        label: "Reminders auto-written and personalized to each customer & invoice",
        standout: true,
    },
    {
        label: "Payment prediction & likelihood-to-pay scoring on every invoice",
        standout: true,
    },
    {
        label: "Next-best-action recommendations for every overdue account",
        standout: true,
    },
    {
        label: "Promise-to-Pay tracking with automatic broken-promise detection & follow-up",
        standout: true,
    },
    {
        label: "Relationship-safety guardrails so you never over-message your best customers",
        standout: true,
    },
    {
        label: "What-if scenario simulation to forecast recovery outcomes",
        standout: true,
    },
    {
        label: "Smart Action Center, a prioritized queue of exactly what needs you next",
    },
    { label: "Dispute & manual-review workflow with full customer context" },
    { label: "Automatic escalation as invoices age" },
    { label: "Hosted pay pages for one-click customer payments" },
    { label: "Customer risk scoring, segmentation & behavior insights" },
    { label: "Reminder delivery tracking with automatic retries" },
    { label: "CSV import to bring your invoices & customers in within minutes" },
    {
        label: "Team seats with role-based access, task assignment & full audit trail",
    },
];

const VALUE_PROPS = [
    {
        icon: <BoltIcon />,
        accent: "#ffaa00",
        title: "No setup fees",
        desc: "Connect your tools and start recovering invoices in minutes, no onboarding charges, ever.",
    },
    {
        icon: <ShieldOutlinedIcon />,
        accent: "#1ec195",
        title: "Cancel anytime",
        desc: "No contracts, and we stop billing the moment you cancel you keep access until the end of the period you paid for. We even keep your account ready for 60 days so you can come back in one click.",
    },
    {
        icon: <SupportAgentOutlinedIcon />,
        accent: "#42a5f5",
        title: "Real human support",
        desc: "Talk to our team whenever you need a hand, included on every plan at no extra cost.",
    },
];

const FAQS = [
    {
        q: "What happens after the 60-day free trial?",
        a: "When your 60-day free trial ends, you can subscribe to the FundsyFi Recover Starter plan at $99/month for your first 2 months. After that you'll continue on the Standard plan at $149/month. We'll remind you before your trial ends, there are no surprise charges.",
    },
    {
        q: "How does the $99 Starter price work?",
        a: "It's a welcome rate: you pay $99/month for your first 2 months of full access. After that you move to the Standard plan at $149/month automatically. The $99 welcome rate applies once per business.",
    },
    {
        q: "Do I need a credit card to start?",
        a: "No. You can explore FundsyFi Recover free for 60 days.without entering any payment details. Add a card only when you're ready to subscribe.",
    },
    {
        q: "Can I cancel anytime? Will I keep being charged?",
        a: "Yes. You can cancel in one click and we won't charge you again. You'll keep full access until the end of the period you've already paid for.",
    },
    {
        q: "What if I cancel and want to come back later?",
        a: "No problem, we keep your account ready for 60 days after you cancel, so you can resubscribe in one click without re-entering your card. Please note the $99/month Starter rate is a one-time welcome offer: if you cancel and resubscribe later, you'll continue on the Standard plan at $149/month.",
    },
    {
        q: "Can I upgrade later?",
        a: "Yes. Higher-volume businesses can upgrade for more automation, more users, and higher message limits as their needs grow.",
    },
];

// ── Page ─────────────────────────────────────────────────────────────────────

const BusinessPricingPage: React.FC = () => {
    const navigate = useNavigate();
    const [showAllFeatures, setShowAllFeatures] = useState(false);
    const ctaRef = useRef<HTMLButtonElement>(null);

    const toggleFeatures = () => {
        setShowAllFeatures((prev) => {
            const next = !prev;
            // When collapsing, bring the “Start free trial” button back into view
            // so the user isn't left stranded further down the page.
            if (!next) {
                requestAnimationFrame(() => {
                    ctaRef.current?.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                    });
                });
            }
            return next;
        });
    };

    // Always land at the top of the page on navigation.
    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }, []);

    return (
        <>
            <SEO
                title="Pricing — FundsyFi Business"
                description="Try FundsyFi Recover free for 60 days. Then just $99/month for your first 2 months, and $149/month after. No credit card to start. Cancel anytime."
            />
            <PageWrapper>
                <BusinessNavbar />

                {/* ── Hero / pricing card ── */}
                <Hero>
                    <GridOverlay />
                    <Orb
                        sx={{
                            top: "-120px",
                            right: "-60px",
                            width: 420,
                            height: 420,
                            background:
                                "radial-gradient(circle, rgba(255,176,0,0.30) 0%, transparent 70%)",
                            animation: `${floatY} 10s ease-in-out infinite`,
                        }}
                    />
                    <Orb
                        sx={{
                            bottom: "-140px",
                            left: "-80px",
                            width: 460,
                            height: 460,
                            background:
                                "radial-gradient(circle, rgba(30,193,149,0.24) 0%, transparent 70%)",
                            animation: `${floatY} 13s ease-in-out infinite`,
                        }}
                    />

                    <Container
                        maxWidth="lg"
                        sx={{ position: "relative", zIndex: 1 }}
                    >
                        <Reveal>
                            <Box sx={{ textAlign: "center", mb: 6 }}>
                                <Badge>
                                    <AutoAwesomeIcon sx={{ fontSize: "0.9rem" }} />
                                    Simple, transparent pricing
                                </Badge>
                                <Typography
                                    sx={{
                                        fontFamily: "'Montserrat', sans-serif",
                                        fontSize: "clamp(2.1rem, 5.4vw, 3.4rem)",
                                        fontWeight: 800,
                                        color: "#fff",
                                        lineHeight: 1.1,
                                        letterSpacing: "-0.03em",
                                        mt: 2.5,
                                        mb: 2,
                                    }}
                                >
                                    Simple pricing,{" "}
                                    <GoldSpan>built for getting paid.</GoldSpan>
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: "'Inter', sans-serif",
                                        fontSize: "clamp(1rem, 1.8vw, 1.15rem)",
                                        color: "rgba(255,255,255,0.66)",
                                        lineHeight: 1.7,
                                        maxWidth: "640px",
                                        mx: "auto",
                                    }}
                                >
                                    Try FundsyFi Recover free for 60 days.
                                    <br />
                                    <br />
                                    Connect your QuickBooks Online account, and
                                    FundsyFi Recover helps automate the rest.
                                    Organizing overdue invoices, sending smart email
                                    and SMS follow-ups, tracking customer replies, and
                                    helping you recover revenue faster.
                                    <br />
                                    <br />
                                    After your trial, get your first 2 months for
                                    $99/month, then continue at $149/month.
                                    <br />
                                    <br />
                                    Cancel anytime.
                                </Typography>
                            </Box>
                        </Reveal>

                        <Reveal delay={0.1}>
                            <Box sx={{ maxWidth: "560px", mx: "auto" }}>
                                <PriceCard>
                                    {/* Highlight ribbon */}
                                    <Box
                                        sx={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 1,
                                            background:
                                                "rgba(30,193,149,0.14)",
                                            border: "1px solid rgba(30,193,149,0.4)",
                                            color: "#34e0ad",
                                            borderRadius: "100px",
                                            px: 2,
                                            py: 0.75,
                                            fontFamily: "'Montserrat', sans-serif",
                                            fontWeight: 700,
                                            fontSize: "0.74rem",
                                            letterSpacing: "0.06em",
                                            textTransform: "uppercase",
                                            mb: 3,
                                        }}
                                    >
                                        <BoltIcon sx={{ fontSize: "0.95rem" }} />
                                        14 days free · then $99/mo for 2 months
                                    </Box>

                                    <Stack
                                        direction="row"
                                        alignItems="flex-end"
                                        spacing={1.2}
                                        sx={{ mb: 0.5 }}
                                    >
                                        <Typography
                                            sx={{
                                                fontFamily:
                                                    "'Montserrat', sans-serif",
                                                fontSize: "clamp(3rem, 8vw, 4.4rem)",
                                                fontWeight: 800,
                                                color: "#fff",
                                                lineHeight: 1,
                                                letterSpacing: "-0.03em",
                                            }}
                                        >
                                            $99
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontFamily: "'Inter', sans-serif",
                                                fontSize: "1.05rem",
                                                color: "rgba(255,255,255,0.6)",
                                                fontWeight: 500,
                                                mb: 1.2,
                                            }}
                                        >
                                            /mo · first 2 months
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontFamily: "'Inter', sans-serif",
                                                fontSize: "1.05rem",
                                                color: "rgba(255,255,255,0.4)",
                                                fontWeight: 500,
                                                mb: 1.2,
                                            }}
                                        >
                                            then $149/mo
                                        </Typography>
                                    </Stack>

                                    <Typography
                                        sx={{
                                            fontFamily: "'Montserrat', sans-serif",
                                            fontWeight: 700,
                                            fontSize: "1.1rem",
                                            color: "#ffcc00",
                                            mb: 2,
                                        }}
                                    >
                                        FundsyFi Recover Starter
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontFamily: "'Inter', sans-serif",
                                            fontSize: "0.95rem",
                                            color: "rgba(255,255,255,0.6)",
                                            lineHeight: 1.6,
                                            mb: 3.5,
                                        }}
                                    >
                                        Start with a 60-day free trial, no card
                                        needed. Then subscribe for $99/month for your
                                        first 2 months, and $149/month after. Cancel
                                        anytime; if you come back later you'll continue
                                        on the $149/month Standard plan.
                                    </Typography>

                                    {/* Feature list */}
                                    <Stack spacing={1.6} sx={{ mb: 2 }}>
                                        {(showAllFeatures
                                            ? INCLUDED_FEATURES
                                            : INCLUDED_FEATURES.slice(0, 5)
                                        ).map((feat) => (
                                            <Stack
                                                key={feat.label}
                                                direction="row"
                                                spacing={1.5}
                                                alignItems="flex-start"
                                            >
                                                <Box
                                                    sx={{
                                                        flexShrink: 0,
                                                        width: 22,
                                                        height: 22,
                                                        borderRadius: "50%",
                                                        background: feat.standout
                                                            ? "rgba(255,204,0,0.16)"
                                                            : "rgba(30,193,149,0.18)",
                                                        border: feat.standout
                                                            ? "1px solid rgba(255,204,0,0.45)"
                                                            : "1px solid rgba(30,193,149,0.4)",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        mt: "1px",
                                                    }}
                                                >
                                                    {feat.standout ? (
                                                        <AutoAwesomeIcon
                                                            sx={{
                                                                fontSize: "0.8rem",
                                                                color: "#ffcc00",
                                                            }}
                                                        />
                                                    ) : (
                                                        <CheckIcon
                                                            sx={{
                                                                fontSize: "0.85rem",
                                                                color: "#34e0ad",
                                                            }}
                                                        />
                                                    )}
                                                </Box>
                                                <Typography
                                                    sx={{
                                                        fontFamily:
                                                            "'Inter', sans-serif",
                                                        fontSize: "0.94rem",
                                                        color: "rgba(255,255,255,0.85)",
                                                        lineHeight: 1.5,
                                                    }}
                                                >
                                                    {feat.label}
                                                </Typography>
                                            </Stack>
                                        ))}
                                    </Stack>

                                    <Button
                                        onClick={toggleFeatures}
                                        endIcon={
                                            showAllFeatures ? (
                                                <ExpandLessIcon />
                                            ) : (
                                                <ExpandMoreIcon />
                                            )
                                        }
                                        sx={{
                                            fontFamily: "'Montserrat', sans-serif",
                                            fontWeight: 700,
                                            fontSize: "0.86rem",
                                            textTransform: "none",
                                            color: "#ffcc00",
                                            px: 0,
                                            mb: 2,
                                            "&:hover": {
                                                background: "transparent",
                                                color: "#ffe05a",
                                            },
                                        }}
                                    >
                                        {showAllFeatures
                                            ? "Show fewer features"
                                            : `See ${INCLUDED_FEATURES.length - 5
                                            } more features`}
                                    </Button>

                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems="flex-start"
                                        sx={{ mb: 3.5 }}
                                    >
                                        <AutoAwesomeIcon
                                            sx={{
                                                fontSize: "0.9rem",
                                                color: "#ffcc00",
                                                mt: "2px",
                                            }}
                                        />
                                        <Typography
                                            sx={{
                                                fontFamily: "'Inter', sans-serif",
                                                fontSize: "0.82rem",
                                                color: "rgba(255,255,255,0.6)",
                                                lineHeight: 1.5,
                                            }}
                                        >
                                            Gold-marked items are smart-automation
                                            features most reminder tools don&apos;t
                                            offer.
                                        </Typography>
                                    </Stack>

                                    <GoldButton
                                        ref={ctaRef}
                                        fullWidth
                                        endIcon={<ArrowForwardIcon />}
                                        onClick={() => navigate("/collect/sign-up")}
                                    >
                                        Start your 60-day free trial
                                    </GoldButton>
                                    <Typography
                                        sx={{
                                            fontFamily: "'Inter', sans-serif",
                                            fontSize: "0.8rem",
                                            color: "rgba(255,255,255,0.45)",
                                            textAlign: "center",
                                            mt: 2,
                                        }}
                                    >
                                        No credit card required · Cancel anytime
                                    </Typography>
                                </PriceCard>

                                <Typography
                                    sx={{
                                        fontFamily: "'Inter', sans-serif",
                                        fontSize: "0.9rem",
                                        color: "rgba(255,255,255,0.6)",
                                        textAlign: "center",
                                        lineHeight: 1.7,
                                        mt: 3,
                                    }}
                                >
                                    Higher-volume businesses can upgrade for more
                                    automation, more users, and higher message
                                    limits.
                                </Typography>
                            </Box>
                        </Reveal>
                    </Container>
                </Hero>

                {/* ── Value props ── */}
                <Section tone="light">
                    <Container maxWidth="lg">
                        <Reveal>
                            <SectionLabel>Why teams choose us</SectionLabel>
                            <SectionTitle>
                                Pricing without the fine print
                            </SectionTitle>
                            <SectionSub>
                                What you see is what you pay. One clear Starter price,
                                friendly support included, and room to upgrade as you
                                grow.
                            </SectionSub>
                        </Reveal>

                        <Grid container spacing={3.5}>
                            {VALUE_PROPS.map((prop, i) => (
                                <Grid item xs={12} md={4} key={prop.title}>
                                    <Reveal delay={i * 0.08}>
                                        <Box
                                            sx={{
                                                background: "#ffffff",
                                                borderRadius: "18px",
                                                p: { xs: "26px 22px", md: "34px 28px" },
                                                border: "1px solid rgba(4,13,69,0.07)",
                                                boxShadow:
                                                    "0 1px 2px rgba(4,13,69,0.04)",
                                                height: "100%",
                                                transition:
                                                    "transform 0.25s ease, box-shadow 0.25s ease",
                                                "&:hover": {
                                                    transform: "translateY(-6px)",
                                                    boxShadow:
                                                        "0 22px 48px rgba(4,13,69,0.12)",
                                                },
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 48,
                                                    height: 48,
                                                    borderRadius: "13px",
                                                    background: `${prop.accent}1a`,
                                                    border: `1px solid ${prop.accent}33`,
                                                    color: prop.accent,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    mb: 2.2,
                                                }}
                                            >
                                                {prop.icon}
                                            </Box>
                                            <Typography
                                                sx={{
                                                    fontFamily:
                                                        "'Montserrat', sans-serif",
                                                    fontWeight: 700,
                                                    fontSize: "1.18rem",
                                                    color: colors.primary,
                                                    mb: 1,
                                                }}
                                            >
                                                {prop.title}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontFamily: "'Inter', sans-serif",
                                                    fontSize: "0.95rem",
                                                    color: "#5b6472",
                                                    lineHeight: 1.7,
                                                }}
                                            >
                                                {prop.desc}
                                            </Typography>
                                        </Box>
                                    </Reveal>
                                </Grid>
                            ))}
                        </Grid>
                    </Container>
                </Section>

                {/* ── FAQ ── */}
                <Section tone="tint">
                    <Container maxWidth="md">
                        <Reveal>
                            <SectionLabel>Pricing FAQ</SectionLabel>
                            <SectionTitle>Questions, answered</SectionTitle>
                            <SectionSub>
                                Everything you need to know about the free trial and
                                your subscription.
                            </SectionSub>
                        </Reveal>

                        <Stack spacing={2}>
                            {FAQS.map((faq, i) => (
                                <Reveal delay={i * 0.06} key={faq.q}>
                                    <Box
                                        sx={{
                                            background: "#ffffff",
                                            borderRadius: "16px",
                                            p: { xs: 2.5, md: 3.2 },
                                            border: "1px solid rgba(4,13,69,0.08)",
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontFamily:
                                                    "'Montserrat', sans-serif",
                                                fontWeight: 700,
                                                fontSize: "1.05rem",
                                                color: colors.primary,
                                                mb: 1,
                                            }}
                                        >
                                            {faq.q}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontFamily: "'Inter', sans-serif",
                                                fontSize: "0.95rem",
                                                color: "#5b6472",
                                                lineHeight: 1.7,
                                            }}
                                        >
                                            {faq.a}
                                        </Typography>
                                    </Box>
                                </Reveal>
                            ))}
                        </Stack>
                    </Container>
                </Section>

                {/* ── Final CTA ── */}
                <Box
                    sx={{
                        position: "relative",
                        overflow: "hidden",
                        background:
                            "linear-gradient(125deg, #070e29 0%, #0d1636 50%, #16245c 100%)",
                        backgroundSize: "200% 200%",
                        animation: `${gradientPan} 18s ease infinite`,
                        py: { xs: 8, md: 12 },
                        px: 2.5,
                        textAlign: "center",
                    }}
                >
                    <Orb
                        sx={{
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%,-50%)",
                            width: 520,
                            height: 320,
                            background:
                                "radial-gradient(circle, rgba(255,176,0,0.16) 0%, transparent 70%)",
                        }}
                    />
                    <Container
                        maxWidth="sm"
                        sx={{ position: "relative", zIndex: 1 }}
                    >
                        <Reveal>
                            <Badge>
                                <AutoAwesomeIcon sx={{ fontSize: "0.9rem" }} />
                                Start free today
                            </Badge>
                            <Typography
                                sx={{
                                    fontFamily: "'Montserrat', sans-serif",
                                    fontSize: "clamp(1.8rem, 4.5vw, 2.8rem)",
                                    fontWeight: 800,
                                    color: "#fff",
                                    lineHeight: 1.14,
                                    letterSpacing: "-0.02em",
                                    mt: 2.5,
                                    mb: 2,
                                }}
                            >
                                Try everything free for{" "}
                                <GoldSpan>14 days</GoldSpan>
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: "'Inter', sans-serif",
                                    fontSize: "1.04rem",
                                    color: "rgba(255,255,255,0.66)",
                                    lineHeight: 1.74,
                                    mb: 4,
                                }}
                            >
                                Then subscribe for just $99/month for your first 2
                                months, and $149/month after. No credit card to start,
                                and cancel anytime.
                            </Typography>
                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={2}
                                justifyContent="center"
                            >
                                <GoldButton
                                    endIcon={<ArrowForwardIcon />}
                                    onClick={() => navigate("/collect/sign-up")}
                                >
                                    Start free trial
                                </GoldButton>
                                <OutlineButton
                                    onClick={() => navigate("/business")}
                                >
                                    Back to overview
                                </OutlineButton>
                            </Stack>
                        </Reveal>
                    </Container>
                </Box>

                <Footer />
            </PageWrapper>
        </>
    );
};

export default BusinessPricingPage;
